import { z } from 'zod';

import type { LedgerAccountType } from './accountTypes';
import type { CurrencyMode } from './currencyCodes';
import { LedgerAccountTypeSchema } from './accountTypes';
import { CurrencyMatchInputSchema, CurrencyModeSchema } from './currencyCodes';
import { ParameterizedString, SafeStringSchema } from './strings';
import { SchemaTxMatchInput } from './tx';

export const LedgerMigrationStatusSchema = z.enum([
  'queued',
  'started',
  'failed',
  'completed',
  'skipped',
]);

export type LedgerMigrationStatus = z.infer<typeof LedgerMigrationStatusSchema>;

export const SafeRecordKey = z
  .string()
  .refine((v) => v !== '__proto__', 'invalid parameter name');

export const ParametersSchema = z.record(
  SafeRecordKey,
  z.string({
    invalid_type_error:
      'Invalid parameter type. All parameters must be string-encoded values.',
  })
);

export const EntryParametersSchema = z.record(
  SafeRecordKey,
  z.union([
    z.string({
      invalid_type_error:
        'Invalid parameter type. Parameters must be string-encoded values or arrays of objects with string values.',
    }),
    z.array(z.record(SafeRecordKey, z.string())),
  ])
);

export type ConsistencyConfig<T extends readonly string[]> = {
  [K in T[number]]?: 'strong' | 'eventual';
};

export const ConsistencyModeSchema = z.enum(['strong', 'eventual']);

export type ConsistencyMode = z.infer<typeof ConsistencyModeSchema>;

export const ConsistencyConfigSchema = <
  T extends readonly string[],
  TExtra extends Parameters<typeof z.object>[0]
>(
  keys: T,
  extra: TExtra
) => {
  const v = z.optional(ConsistencyModeSchema);
  const schemaObject = Object.fromEntries(
    keys.map((key) => [key, v])
  ) as Record<T[number], typeof v>;

  return z.object({ ...extra, ...schemaObject });
};

const GroupConsistencyConfig = z.object({
  key: SafeStringSchema,
  ownBalanceUpdates: ConsistencyModeSchema,
});

type GroupConsistencyConfigType = z.infer<typeof GroupConsistencyConfig>;

export type AccountConsistencyConfig = {
  lines?: ConsistencyMode;
  ownBalanceUpdates?: ConsistencyMode;
  totalBalanceUpdates?: ConsistencyMode;
  groups?: GroupConsistencyConfigType[];
};

export const AccountConsistencyConfigSchema: z.ZodType<
  AccountConsistencyConfig,
  z.ZodTypeDef,
  any
> = z.object({
  lines: z.optional(ConsistencyModeSchema),
  ownBalanceUpdates: z.optional(ConsistencyModeSchema),
  totalBalanceUpdates: z.optional(ConsistencyModeSchema),
  groups: z.optional(z.array(GroupConsistencyConfig)),
});

export const SchemaCurrencyMatchInput = z.object({
  customCurrencyId: z.string().optional(),
  code: z.string(),
});

export const SchemaLinkTypeList = [
  'IncreaseLink',
  'UnitLink',
  'CustomLink',
  'StripeLink',
] as const;

export const SchemaLinkType = z.enum(SchemaLinkTypeList);

export type SchemaLinkTypeType = z.infer<typeof SchemaLinkType>;

export const SchemaExternalAccountMatchInput = z.object({
  linkType: SchemaLinkType.optional(),
  linkIk: z.string().optional(),
  id: z.string().optional(),
  externalId: z.string().optional(),
  linkId: z.string().optional(),
});

export type SchemaExternalAccountMatchInputType = z.infer<
  typeof SchemaExternalAccountMatchInput
>;

export const SchemaPaymentInput = z.object({
  enabled: z.boolean(),
});

export type SchemaPaymentInputType = z.infer<typeof SchemaPaymentInput>;

export const SchemaLedgerEntityStatusSchema = z.enum([
  'active',
  'disabled',
  'archived',
]);

export type SchemaLedgerEntityStatus = z.infer<
  typeof SchemaLedgerEntityStatusSchema
>;

export type BaseSchemaLedgerAccountInputType = {
  children?: BaseSchemaLedgerAccountInputType[];
  key: string;
  name?: string;
  type?: LedgerAccountType;
  consistencyConfig?: AccountConsistencyConfig;
  currency?: z.infer<typeof SchemaCurrencyMatchInput>;
  currencyMode?: CurrencyMode;
  template?: boolean;
  clearing?: boolean;
  linkedAccount?: z.infer<typeof SchemaExternalAccountMatchInput>;
  payment?: SchemaPaymentInputType;
  status?: SchemaLedgerEntityStatus;
};

export const BaseSchemaLedgerAccountInput: z.ZodType<BaseSchemaLedgerAccountInputType> =
  z.lazy(() =>
    z.object({
      key: z.string(),
      name: z.string().optional(),
      type: LedgerAccountTypeSchema.optional(),
      currency: SchemaCurrencyMatchInput.optional(),
      currencyMode: CurrencyModeSchema.optional(),
      template: z.boolean().optional(),
      clearing: z.boolean().optional(),
      children: z.array(BaseSchemaLedgerAccountInput).optional(),
      linkedAccount: SchemaExternalAccountMatchInput.optional(),
      payment: SchemaPaymentInput.optional(),
      consistencyConfig: AccountConsistencyConfigSchema.optional(),
      status: SchemaLedgerEntityStatusSchema.optional(),
    })
  );
export const BaseChartOfAccounts = z.object({
  defaultConsistencyConfig: AccountConsistencyConfigSchema.optional(),
  defaultCurrency: z.optional(CurrencyMatchInputSchema),
  defaultCurrencyMode: CurrencyModeSchema.optional(),
  accounts: z.array(BaseSchemaLedgerAccountInput),
});

export type BaseChartOfAccountsType = z.infer<typeof BaseChartOfAccounts>;

export const SchemaCondition = z.object({
  ownBalance: z
    .object({
      eq: z.string().optional(),
      lte: z.string().optional(),
      gte: z.string().optional(),
    })
    .optional(),
  totalBalance: z
    .object({
      eq: z.string().optional(),
      lte: z.string().optional(),
      gte: z.string().optional(),
    })
    .optional(),
});

export type BaseSchemaLedgerEntryConditionInputType = {
  account?: {
    path: string;
  };
  postcondition?: z.infer<typeof SchemaCondition>;
  precondition?: z.infer<typeof SchemaCondition>;
  currency?: z.infer<typeof SchemaCurrencyMatchInput>;
  repeated?: { key: z.infer<typeof SafeStringSchema> };
};

export type BaseSchemaLedgerLineTagInputType = {
  key: string;
  value: string;
};

export type BaseSchemaLedgerEntryLineInputType = {
  key: string;
  account?: {
    path: string;
  };
  amount?: string;
  currency?: z.infer<typeof SchemaCurrencyMatchInput>;
  description?: string;
  tx?: z.infer<typeof SchemaTxMatchInput>;
  tags?: BaseSchemaLedgerLineTagInputType[];
  repeated?: { key: z.infer<typeof SafeStringSchema> };
};

export type BaseSchemaLedgerEntryTagInputType = {
  key: string;
  value: string;
};

export type BaseSchemaLedgerEntryGroupInputType = {
  key: z.infer<typeof SafeStringSchema>;
  value: string;
};

export const PostLinesAsSchema = z.enum([
  'raw_lines',
  'net_amounts',
  'skip_zero_lines',
]);
export type PostLinesAs = z.infer<typeof PostLinesAsSchema>;

// Flat condition shape used by the MCP designer interface.
// The canonical schema stores conditions as nested precondition/postcondition
// objects; the MCP interface flattens them into these three enums for AI use.
export const conditionTypes = ['pre', 'post'] as const;
export type ConditionType = (typeof conditionTypes)[number];

export const conditionFields = ['totalBalance', 'ownBalance'] as const;
export type ConditionField = (typeof conditionFields)[number];

export const conditionOperators = ['gte', 'lte', 'eq'] as const;
export type ConditionOperator = (typeof conditionOperators)[number];

export type BaseSchemaLedgerEntryInputType = {
  type: string;
  typeVersion?: number;
  description?: string;
  conditions?: BaseSchemaLedgerEntryConditionInputType[];
  lines?: BaseSchemaLedgerEntryLineInputType[];
  parameters?: Record<string, string>;
  tags?: BaseSchemaLedgerEntryTagInputType[];
  groups?: BaseSchemaLedgerEntryGroupInputType[];
  status?: SchemaLedgerEntityStatus;
  postLinesAs?: PostLinesAs;
};

export const BaseSchemaLedgerEntryConditionInput = z.object({
  account: z.object({ path: z.string() }).optional(),
  postcondition: SchemaCondition.optional(),
  precondition: SchemaCondition.optional(),
  currency: SchemaCurrencyMatchInput.optional(),
  repeated: z
    .object({
      key: SafeStringSchema.refine((k) => k.trim().length >= 1, {
        message: 'repeated.key cannot be empty',
      }),
    })
    .optional(),
}) as z.ZodType<BaseSchemaLedgerEntryConditionInputType>;

const BaseSchemaLedgerLineTagInput = z.object({
  key: z.string(),
  value: z.string(),
});

export const BaseSchemaLedgerEntryLineInput = z.object({
  account: z.object({ path: z.string() }).optional(),
  amount: z.string().optional(),
  key: z.string(),
  currency: SchemaCurrencyMatchInput.optional(),
  description: z.string().optional(),
  tx: SchemaTxMatchInput.optional(),
  tags: z.array(BaseSchemaLedgerLineTagInput).optional(),
  repeated: z
    .object({
      key: SafeStringSchema.refine((k) => k.trim().length >= 1, {
        message: 'repeated.key cannot be empty',
      }),
    })
    .optional(),
});

export const BaseSchemaLedgerEntryInput = z.object({
  type: z.string(),
  typeVersion: z.number().int().optional(),
  description: z.string().optional(),
  conditions: z.array(BaseSchemaLedgerEntryConditionInput).optional(),
  lines: z.array(BaseSchemaLedgerEntryLineInput).optional(),
  parameters: z.record(SafeRecordKey, z.string()).optional(),
  tags: z
    .array(
      z.object({
        key: z.string(),
        value: z.string(),
      })
    )
    .optional(),
  groups: z
    .array(
      z.object({
        key: SafeStringSchema,
        value: z.string(),
      })
    )
    .optional(),
  status: SchemaLedgerEntityStatusSchema.optional(),
  postLinesAs: PostLinesAsSchema.optional(),
});

export const BaseLedgerEntries = z.object({
  types: z.array(BaseSchemaLedgerEntryInput),
});

/**
 * Payment lifecycle transitions that can carry an embedded ledger entry in a
 * payment type's `accounting` block.
 *
 * Values are persisted: as `accounting` keys in stored schemas and Liveblocks
 * rooms, and inside the LedgerEntry `type` that `makePaymentEntryType` derives.
 */
// Declaration order is lifecycle order; consumers rely on
// Object.values(PaymentAccountingEventKey) for event ordering (designer tabs,
// scenario row insertion).
export const PaymentAccountingEventKey = {
  /** The payment was captured and is guaranteed to settle. */
  initiated: 'initiated',
  settled: 'settled',
  /** A refund of the settled payment is initiated. */
  refund_initiated: 'refund_initiated',
  /** An initiated refund failed, so the refunded funds return. */
  refund_failed: 'refund_failed',
} as const;
export const PaymentAccountingEventKeySchema = z.nativeEnum(
  PaymentAccountingEventKey
);
export type PaymentAccountingEventKey = z.infer<
  typeof PaymentAccountingEventKeySchema
>;

export type OptionalPaymentAccountingEventKey = Exclude<
  PaymentAccountingEventKey,
  typeof PaymentAccountingEventKey.settled
>;

export const isOptionalPaymentAccountingEventKey = (
  event: PaymentAccountingEventKey
): event is OptionalPaymentAccountingEventKey =>
  event !== PaymentAccountingEventKey.settled;

/** The `accounting` block of a Payment Type, where only `settled` is required. */
export type PaymentAccounting<T> = {
  [PaymentAccountingEventKey.settled]: T;
} & Partial<Record<OptionalPaymentAccountingEventKey, T>>;

type RefundPaymentAccountingEventKey =
  | typeof PaymentAccountingEventKey.refund_initiated
  | typeof PaymentAccountingEventKey.refund_failed;

// TODO(FRA-7942): Remove the header gate once refunds launch.
/**
 * Tells whether `event` is a refund event. storeSchema accepts refund events
 * only behind the `x-fragment-allow-refunds` request header.
 */
export const isRefundPaymentAccountingEventKey = (
  event: PaymentAccountingEventKey
): event is RefundPaymentAccountingEventKey =>
  event === PaymentAccountingEventKey.refund_initiated ||
  event === PaymentAccountingEventKey.refund_failed;

export const PaymentTypeDirection = {
  payin: 'payin',
  payout: 'payout',
} as const;
export const PaymentTypeDirectionSchema = z.nativeEnum(PaymentTypeDirection);
export type PaymentTypeDirection = z.infer<typeof PaymentTypeDirectionSchema>;

/**
 * System-owned lines in a payment entry. The runtime fills their amounts by
 * system identity at post time; the designer locks them from editing.
 */
export const SystemLineKind = {
  payment_settlement_line: 'payment_settlement_line',
  payment_fee_line: 'payment_fee_line',
  payment_refund_line: 'payment_refund_line',
  payment_refund_reversal_line: 'payment_refund_reversal_line',
} as const;
export const SystemLineKindSchema = z.nativeEnum(SystemLineKind);
export type SystemLineKind = z.infer<typeof SystemLineKindSchema>;

/**
 * Hard-coded v1 parameter names each system line's amount references. The
 * runtime fills values by system identity into these names; values are always
 * positive — negation happens in the line expression.
 */
export const SystemLineParameterName = {
  payment_settlement_line: 'settled_amount',
  payment_fee_line: 'fragment_fee_amount',
  payment_refund_line: 'refund_amount',
  payment_refund_reversal_line: 'refund_amount',
} as const satisfies Record<SystemLineKind, string>;

/**
 * The exact amount expression each system line must carry. System parameter
 * values are always positive at runtime — the fee is negated in the line.
 * Shared by the designer's default payment entry and the schema validators.
 */
export const SYSTEM_LINE_AMOUNT = {
  [SystemLineKind.payment_settlement_line]: `{{${SystemLineParameterName.payment_settlement_line}}}`,
  [SystemLineKind.payment_fee_line]: `-{{${SystemLineParameterName.payment_fee_line}}}`,
  [SystemLineKind.payment_refund_line]: `-{{${SystemLineParameterName.payment_refund_line}}}`,
  [SystemLineKind.payment_refund_reversal_line]: `{{${SystemLineParameterName.payment_refund_reversal_line}}}`,
} as const satisfies Record<SystemLineKind, string>;

/** The system parameter names, for membership checks against user input. */
export const SYSTEM_PARAMETER_NAMES: ReadonlySet<string> = new Set(
  Object.values(SystemLineParameterName)
);

export const BaseSchemaPaymentInput = z.object({
  amount: z.string(),
  direction: PaymentTypeDirectionSchema,
});

// A strict subset of ledger lines: no tx, tags, or repeated, and amount and
// account are always required — payment entries have no runtime-filled
// (reconcileTx) variant, and designer drafts are validated through the
// all-optional PaymentLineValidationInput rather than this schema.
export const BaseSchemaPaymentEntryLineInput =
  BaseSchemaLedgerEntryLineInput.pick({
    key: true,
    currency: true,
    description: true,
  }).extend({
    account: z.object({ path: z.string() }),
    amount: z.string(),
    system: SystemLineKindSchema.optional(),
  });

/**
 * The ledger entry a payment type posts on a lifecycle event. A strict subset
 * of the entry-version attributes — description and lines only; not an entry
 * type (no `type` string).
 */
export const BaseSchemaPaymentEntryInput = z.object({
  description: z.string().optional(),
  lines: z.array(BaseSchemaPaymentEntryLineInput),
});

// Narrower than SchemaLedgerEntityStatusSchema: payment types only support
// `active` for now, and widening later is easier than reversing a public enum.
export const SchemaPaymentTypeStatusSchema = z.enum(['active']);
export type SchemaPaymentTypeStatus = z.infer<
  typeof SchemaPaymentTypeStatusSchema
>;

export const BaseSchemaPaymentTypeInput = z.object({
  type: z.string(),
  typeVersion: z.number().int(),
  status: SchemaPaymentTypeStatusSchema,
  payment: BaseSchemaPaymentInput,
  accounting: z.object({
    initiated: BaseSchemaPaymentEntryInput.optional(),
    settled: BaseSchemaPaymentEntryInput,
    refund_initiated: BaseSchemaPaymentEntryInput.optional(),
    refund_failed: BaseSchemaPaymentEntryInput.optional(),
  }),
});

export const BaseSchemaPayments = z.object({
  types: z.array(BaseSchemaPaymentTypeInput),
});

export type BaseSchemaPaymentsType = z.infer<typeof BaseSchemaPayments>;
export type BaseSchemaPaymentTypeInputType = z.infer<
  typeof BaseSchemaPaymentTypeInput
>;
export type BaseSchemaPaymentInputType = z.infer<typeof BaseSchemaPaymentInput>;
export type BaseSchemaPaymentEntryInputType = z.infer<
  typeof BaseSchemaPaymentEntryInput
>;
export type BaseSchemaPaymentEntryLineInputType = z.infer<
  typeof BaseSchemaPaymentEntryLineInput
>;

export const SceneEntrySchema = z.object({
  type: z.string(),
  typeVersion: z.number().int().positive().optional(),
  parameters: z.record(SafeRecordKey, z.string()),
});

export type SceneEntryType = z.infer<typeof SceneEntrySchema>;

/**
 * A simulated payment declared by a Scene, addressed by its `ik`.
 *
 * The payment's address and parameters live here once; its lifecycle events
 * reference the `ik`, so two events of one payment cannot disagree.
 */
export const ScenePaymentSchema = z.object({
  ik: z.string(),
  type: z.string(),
  typeVersion: z.number().int().positive().optional(),
  parameters: z.record(SafeRecordKey, z.string()),
});

export type ScenePaymentType = z.infer<typeof ScenePaymentSchema>;

export const SceneLedgerEventSchema = z.object({
  eventType: z.literal('entry'),
  entry: SceneEntrySchema,
});

export type SceneLedgerEventType = z.infer<typeof SceneLedgerEventSchema>;

export const ScenePaymentEventSchema = z.object({
  eventType: z.literal('payment'),
  payment: z.object({
    ik: z.string(),
    event: PaymentAccountingEventKeySchema,
  }),
});

export type ScenePaymentEventType = z.infer<typeof ScenePaymentEventSchema>;

export const SceneEventSchema = z.discriminatedUnion('eventType', [
  SceneLedgerEventSchema,
  ScenePaymentEventSchema,
]);

export type SceneEventType = z.infer<typeof SceneEventSchema>;

export const BaseScene = z.object({
  name: z.string(),
  events: z.array(SceneEventSchema),
  payments: z.array(ScenePaymentSchema).optional(),
});

export type BaseSceneType = z.infer<typeof BaseScene>;

export const EntryKey = z.object({
  type: SafeStringSchema,
  typeVersion: z.number().int().positive(),
});
export type EntryKeyType = z.infer<typeof EntryKey>;

export const SchemaLedgerAccountMatchInput = z.object({
  path: ParameterizedString,
});
export type SchemaLedgerAccountMatchInputType = z.infer<
  typeof SchemaLedgerAccountMatchInput
>;

export const GroupReconciliationParameters = z.object({
  clearingAccountPath: SchemaLedgerAccountMatchInput,
});
export type GroupReconciliationParametersType = z.infer<
  typeof GroupReconciliationParameters
>;

export const SchemaEntryGroup = z.object({
  key: SafeStringSchema,
  description: z.string().optional(),
  reconciliation: GroupReconciliationParameters.optional(),
});

export type SchemaEntryGroupType = z.infer<typeof SchemaEntryGroup>;

export const BaseSchema = z.object({
  name: z.string().optional(),
  key: z.string(),
  chartOfAccounts: BaseChartOfAccounts,
  ledgerEntries: BaseLedgerEntries.optional(),
  payments: BaseSchemaPayments.optional(),
  groups: z.array(SchemaEntryGroup).optional(),
  scenes: z.array(BaseScene).optional(),
  consistencyConfig: z.optional(
    z.object({
      entries: ConsistencyModeSchema.optional(),
    })
  ),
});

export type BaseSchemaType = z.infer<typeof BaseSchema>;
export type BaseSchemaLedgerAccountType =
  BaseSchemaType['chartOfAccounts']['accounts'][number];

export const VALID_LEDGER_ACCOUNT_STATUS_TRANSITIONS: Record<
  SchemaLedgerEntityStatus,
  SchemaLedgerEntityStatus[]
> = {
  active: ['disabled'],
  disabled: ['archived', 'active'],
  archived: ['active'],
};
